import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, INCOME_SOURCES, TIME_PERIODS } from '../lib/utils';
import { Filter, DollarSign, Calendar as CalendarRange } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import ExportButton from '../components/ExportButton';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  startOfDay, startOfWeek, startOfMonth, startOfYear,
  endOfDay, endOfWeek, endOfMonth, endOfYear,
  isWithinInterval, parseISO, format,
} from 'date-fns';
import { toast } from 'sonner';
import { IncomeFormDialog } from '../components/finance/IncomeFormDialog';
import { IncomeTable } from '../components/finance/IncomeTable';
import { EMPTY_INCOME_FORM, incomeItemToForm, buildIncomePayload } from '../components/finance/incomeFormUtils';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getDateRange = (period) => {
  const now = new Date();
  switch (period) {
    case 'daily':
      return { startDate: startOfDay(now), endDate: endOfDay(now), label: format(now, 'EEEE, MMMM d, yyyy') };
    case 'weekly': {
      const startDate = startOfWeek(now, { weekStartsOn: 1 });
      const endDate = endOfWeek(now, { weekStartsOn: 1 });
      return { startDate, endDate, label: `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}` };
    }
    case 'monthly':
      return { startDate: startOfMonth(now), endDate: endOfMonth(now), label: format(now, 'MMMM yyyy') };
    case 'yearly':
      return { startDate: startOfYear(now), endDate: endOfYear(now), label: format(now, 'yyyy') };
    default:
      return { startDate: null, endDate: null, label: 'All Time' };
  }
};

const IncomePage = () => {
  const { hasRole } = useAuth();
  const [income, setIncome] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filterSource, setFilterSource] = useState('all');
  const [timePeriod, setTimePeriod] = useState('monthly');
  const [formData, setFormData] = useState({ ...EMPTY_INCOME_FORM, date: new Date() });

  const resetForm = () => {
    setFormData({ ...EMPTY_INCOME_FORM, date: new Date() });
    setEditingId(null);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setFormData(incomeItemToForm(item));
    setDialogOpen(true);
  };

  // Receipt upload (compresses images, accepts PDFs as raw base64)
  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    try {
      let dataUrl;
      if (file.type.startsWith('image/')) {
        const { compressImageToBase64 } = await import('../lib/imageCompress');
        dataUrl = await compressImageToBase64(file, { maxDimension: 1280, quality: 0.75 });
      } else {
        dataUrl = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = rej;
          r.readAsDataURL(file);
        });
      }
      setFormData((f) => ({ ...f, receipt_image: dataUrl, document_name: file.name }));
      toast.success(`Attached ${file.name}`);
    } catch (err) {
      toast.error('Failed to attach file');
    }
  };

  useEffect(() => {
    fetchIncome();
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSource]);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/projects`);
      setProjects(res.data);
    } catch (e) { toast.error('Failed to load projects'); }
  };

  const fetchIncome = async () => {
    try {
      let url = `${API_URL}/api/income`;
      if (filterSource && filterSource !== 'all') {
        url += `?source=${filterSource}`;
      }
      const response = await axios.get(url);
      setIncome(response.data);
    } catch (error) {
      toast.error('Failed to fetch income data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = buildIncomePayload(formData);
      if (editingId) {
        await axios.put(`${API_URL}/api/income/${editingId}`, payload);
        toast.success('Income updated successfully');
      } else {
        await axios.post(`${API_URL}/api/income`, payload);
        toast.success('Income recorded successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchIncome();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingId ? 'update' : 'record'} income`);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/income/${id}`);
      toast.success('Income deleted');
      setDeleteTarget(null);
      fetchIncome();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete income');
    }
  };

  const { startDate, endDate, label: periodLabel } = getDateRange(timePeriod);
  const filteredIncome = (!startDate || !endDate) ? income : income.filter((item) =>
    isWithinInterval(parseISO(item.date), { start: startDate, end: endDate })
  );
  const totalIncome = filteredIncome.reduce((sum, item) => sum + item.amount, 0);
  const allTimeTotal = income.reduce((sum, item) => sum + item.amount, 0);
  const canEdit = hasRole(['admin', 'finance_officer']);

  const incomeBySource = filteredIncome.reduce((acc, item) => {
    const source = item.source.replace('_', ' ');
    acc[source] = (acc[source] || 0) + item.amount;
    return acc;
  }, {});
  const chartData = Object.entries(incomeBySource).map(([name, value]) => ({ name, value }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-semibold">Income Management</h2>
          <p className="text-muted-foreground">Track all income sources</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            path="exports/financial-report"
            filenameBase="suubi_financial_report"
            label="Export"
            testId="income-export-btn"
          />
          {canEdit && (
          <IncomeFormDialog
            open={dialogOpen}
            onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            editingId={editingId}
            projects={projects}
            onReceiptUpload={handleReceiptUpload}
            onTriggerClick={resetForm}
          />
        )}
        </div>
      </div>

      {/* Time Period Tabs with Date Range Display */}
      <div className="space-y-2">
        <Tabs value={timePeriod} onValueChange={setTimePeriod} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-4" data-testid="income-period-tabs">
            {TIME_PERIODS.map((period) => (
              <TabsTrigger key={period.value} value={period.value} data-testid={`period-${period.value}`}>
                {period.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2 text-sm">
          <CalendarRange className="h-4 w-4 text-primary" />
          <span className="font-medium text-primary">{periodLabel}</span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10">
                <DollarSign className="h-8 w-8 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground capitalize">{timePeriod} Income</p>
                <p className="text-3xl font-heading font-bold text-primary">{formatCurrency(totalIncome)}</p>
                <p className="text-xs text-muted-foreground mt-1">{periodLabel}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/20">
                <CalendarRange className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">All Time Total</p>
                <p className="text-3xl font-heading font-bold text-green-600">{formatCurrency(allTimeTotal)}</p>
                <p className="text-xs text-muted-foreground mt-1">{income.length} total records</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Income by Source Chart */}
      {chartData.length > 0 && (
        <Card className="card-hover">
          <CardHeader>
            <CardTitle className="font-heading capitalize">{timePeriod} Income by Source</CardTitle>
            <p className="text-sm text-muted-foreground">{periodLabel}</p>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`} />
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  />
                  <Bar dataKey="value" fill="#2E7D32" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger className="w-48" data-testid="income-filter">
            <SelectValue placeholder="Filter by source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {INCOME_SOURCES.map((source) => (
              <SelectItem key={source.value} value={source.value}>
                {source.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Badge variant="outline" className="ml-auto">
          {filteredIncome.length} records for {periodLabel}
        </Badge>
      </div>

      <IncomeTable
        income={filteredIncome}
        projects={projects}
        loading={loading}
        periodLabel={periodLabel}
        canEdit={canEdit}
        onEdit={openEdit}
        onDelete={setDeleteTarget}
      />

      {/* Delete confirmation — replaces unreliable window.confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-income-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete income entry?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget ? (
                <>Are you sure you want to delete{' '}
                <strong>{formatCurrency(deleteTarget.amount)}</strong>{' '}
                ({deleteTarget.source?.replace(/_/g, ' ')})
                {deleteTarget.description ? ` — "${deleteTarget.description}"` : ''}?
                This action cannot be undone.</>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-income-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && handleDelete(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-income-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default IncomePage;
